import type { Schemas } from "@softmax/observatory-client/types";
import { buildHref } from "@softmax/web-addresses";

import { type GamePageContext } from "../../game-types";
import type { GamePagePath } from "./path";

export type SelectedGameUpload = Schemas["CoworldUploadResponse"] | null;

export type ResolvedGamePageInput = {
  readonly path: GamePagePath;
  readonly resolution: Schemas["GameAddressRouting"];
  readonly selectedUpload: SelectedGameUpload;
  readonly requestedLeague: string | null;
  readonly defaultFallback: boolean;
  readonly viewer: GamePageContext["viewer"];
  readonly frontDoor: () => Promise<string | null>;
};

async function canonicalLeagueSegment({
  resolution,
  requestedLeague,
  defaultFallback,
  frontDoor,
}: Omit<ResolvedGamePageInput, "path" | "selectedUpload" | "viewer">): Promise<
  string | null
> {
  const address = resolution.game_address;
  const shortName = resolution.league?.short_name ?? null;
  let rootLeague = false;

  // The front-door league uses the game root when their slugs match; other leagues keep their segment.
  if (shortName === address.slug) {
    rootLeague =
      !requestedLeague ||
      defaultFallback ||
      (await frontDoor()) === resolution.league?.id;
  }

  return shortName && !rootLeague ? shortName : null;
}

export async function canonicalGamePath({
  path,
  resolution,
  requestedLeague,
  defaultFallback,
  frontDoor,
}: Omit<ResolvedGamePageInput, "selectedUpload" | "viewer">): Promise<string> {
  const address = resolution.game_address;
  const { route } = path;
  const tail = [...path.tail];
  const shortName = resolution.league?.short_name ?? null;
  const leagueSegment = await canonicalLeagueSegment({
    resolution,
    requestedLeague,
    defaultFallback,
    frontDoor,
  });

  if (requestedLeague && !defaultFallback && shortName) {
    if (leagueSegment) tail[0] = encodeURIComponent(leagueSegment);
    else tail.shift();
  }

  if (
    resolution.league?.short_name &&
    (route.name === "game.play" ||
      route.name === "game.playEpisodes" ||
      route.name === "game.playRequests" ||
      route.name === "game.playActivity")
  ) {
    return buildHref({
      name: route.name,
      game: address,
      ...(leagueSegment ? { league: leagueSegment } : {}),
    });
  }

  const publicBase = buildHref({ name: "game.home", game: address });
  return `${publicBase}${tail.length ? `/${tail.join("/")}` : ""}`;
}
